import { useCallback, type ReactNode, useState } from "react";
import type { GrantRow } from "../../shared/rbac/crossResourceAvailability";
import type { GrantEntry } from "../../shared/rbac/roles";
import { ConfirmModal } from "../components/ConfirmModal";
import {
  Count,
  type DisconnectImpact,
  DisconnectWarning,
} from "../components/DisconnectWarning";
import {
  useAffectedChatCount,
  useConnectorDisconnectImpact,
} from "./useConnectorDisconnectImpact";

// Guards a connector grant commit behind a confirmation when the change would
// disconnect the connector from workflows or git sync in spaces losing access.
// Commits with no visible impact proceed silently.
export function useConnectorDisconnectGuard({
  appId,
  appName,
  scopeGrants,
  ready,
}: {
  appId: string;
  appName: string;
  scopeGrants: readonly GrantRow[];
  ready: boolean;
}): {
  guardCommit: (
    grants: GrantEntry[],
    proceed: (grants: GrantEntry[]) => void
  ) => void;
  modal: ReactNode;
  loaded: boolean;
} {
  const [computeImpact, computeImpactLoaded] =
    useConnectorDisconnectImpact(appId);
  const [pending, setPending] = useState<{
    grants: GrantEntry[];
    proceed: (grants: GrantEntry[]) => void;
    impact: DisconnectImpact;
  } | null>(null);

  const loaded = ready && computeImpactLoaded;
  const guardCommit = useCallback(
    (grants: GrantEntry[], proceed: (grants: GrantEntry[]) => void) => {
      if (!loaded) return;
      const impact = computeImpact(scopeGrants, grants);
      if (impact.workflows.length === 0 && impact.gitSpaceNames.length === 0) {
        proceed(grants);
        return;
      }
      setPending({ grants, proceed, impact });
    },
    [computeImpact, scopeGrants, loaded]
  );

  const impact = pending?.impact;
  const workflowCount = impact?.workflows.length ?? 0;
  const hasWorkflows = workflowCount > 0;
  const [chatCount, chatCountLoaded] = useAffectedChatCount(
    appId,
    impact?.affectedUserIds ?? []
  );
  const modal = (
    <ConfirmModal
      open={pending !== null}
      onClose={() => setPending(null)}
      onConfirm={() => {
        if (!loaded || !chatCountLoaded) return;
        pending?.proceed(pending.grants);
        setPending(null);
      }}
      title={
        hasWorkflows
          ? "Remove connector from workflows?"
          : "Remove connector from git sync?"
      }
      description={
        hasWorkflows ? (
          <>
            Removing this access means{" "}
            <Count n={workflowCount} noun="workflow" /> will need a connector
            replaced.
          </>
        ) : (
          `This removes ${appName} from git sync.`
        )
      }
      confirmLabel="Remove access"
      confirmDisabled={!loaded || !chatCountLoaded}
      destructive
    >
      {impact && <DisconnectWarning {...impact} chatCount={chatCount} />}
    </ConfirmModal>
  );

  return { guardCommit, modal, loaded };
}
